import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import {
  CachedEntry,
  IndexedAsyncStorageCache,
} from '@/services/_shared/IndexedAsyncStorageCache';

export interface CachedContent {
  data: any;
  timestamp: number;
  campaignId: string;
  ttl: number;
}

interface CacheConfig {
  maxEntries: number;
  defaultTTL: number;
  cleanupInterval: number;
}

const CACHE_PREFIX = '@content_cache_';
const CACHE_INDEX_KEY = '@content_cache_index';

const DEFAULT_CONFIG: CacheConfig = {
  maxEntries: 10000,
  defaultTTL: 24 * 60 * 60 * 1000,
  cleanupInterval: 60 * 60 * 1000,
};

const CACHE_TTL_CONFIG = {
  series: 24 * 60 * 60 * 1000,
  rubriques: 24 * 60 * 60 * 1000,
  episodes: 12 * 60 * 60 * 1000,
  seriesByRubrique: 6 * 60 * 60 * 1000,
  featured: 1 * 60 * 60 * 1000,
};

class ContentCacheService extends IndexedAsyncStorageCache<CachedContent> {
  private config: CacheConfig = DEFAULT_CONFIG;
  private cleanupTimerId: ReturnType<typeof setInterval> | null = null;

  constructor() {
    super({
      indexKey: CACHE_INDEX_KEY,
      logTag: '[CONTENT-CACHE]',
      defaultTtlMs: DEFAULT_CONFIG.defaultTTL,
      quotaEvictionBatch: 20,
      maxQuotaRetries: 3,
      cleanupOnInit: true,
    });
  }

  async initialize(): Promise<void> {
    if (this.initialized) return;
    await super.initialize();
    if (!this.cleanupTimerId) {
      this.cleanupTimerId = setInterval(() => {
        this.cleanupExpired().catch((err) =>
          console.error('[CONTENT-CACHE] Error in cleanup interval:', err)
        );
      }, this.config.cleanupInterval);
    }
  }

  private generateCacheKey(key: string, campaignId: string): string {
    return `${CACHE_PREFIX}${campaignId}_${key}`;
  }

  async get<T>(key: string, campaignId: string): Promise<T | null> {
    await this.initialize();
    const cacheKey = this.generateCacheKey(key, campaignId);
    try {
      const cached = await AsyncStorage.getItem(cacheKey);
      if (!cached) return null;
      const cachedContent: CachedContent = JSON.parse(cached);
      const age = Date.now() - cachedContent.timestamp;
      if (age > cachedContent.ttl) {
        await this.remove(key, campaignId);
        return null;
      }
      return cachedContent.data as T;
    } catch (error) {
      console.error(`[CONTENT-CACHE] Error reading cache for key "${key}":`, error);
      return null;
    }
  }

  async set<T>(
    key: string,
    campaignId: string,
    data: T,
    ttl: number = this.config.defaultTTL
  ): Promise<void> {
    await this.initialize();
    const cacheKey = this.generateCacheKey(key, campaignId);
    try {
      if (this.quotaExhausted && Platform.OS === 'web') return;

      const cachedContent: CachedContent = {
        data,
        timestamp: Date.now(),
        campaignId,
        ttl,
      };

      const stored = await this.setItemWithQuotaHandling(cacheKey, JSON.stringify(cachedContent));
      if (!stored) return;

      this.cacheIndex.add(cacheKey);

      if (this.cacheIndex.size > this.config.maxEntries) {
        await this.enforceMaxEntries();
      }

      await this.persistIndex();
    } catch (error) {
      console.error(`[CONTENT-CACHE] Error storing cache for key "${key}":`, error);
    }
  }

  async remove(key: string, campaignId: string): Promise<void> {
    await this.initialize();
    const cacheKey = this.generateCacheKey(key, campaignId);
    try {
      await AsyncStorage.removeItem(cacheKey);
      this.cacheIndex.delete(cacheKey);
      await this.persistIndex();
    } catch (error) {
      console.error(`[CONTENT-CACHE] Error removing cache for key "${key}":`, error);
    }
  }

  async clear(): Promise<void> {
    await super.clear();
    this.quotaExhausted = false;
  }

  async clearCampaign(campaignId: string): Promise<void> {
    await this.initialize();
    try {
      const keysToRemove = [...this.cacheIndex].filter((key) =>
        key.includes(`${CACHE_PREFIX}${campaignId}_`)
      );
      if (keysToRemove.length > 0) {
        await AsyncStorage.multiRemove(keysToRemove);
        keysToRemove.forEach((key) => this.cacheIndex.delete(key));
        await this.persistIndex();
      }
    } catch (error) {
      console.error('[CONTENT-CACHE] Error clearing campaign cache:', error);
    }
  }

  private async enforceMaxEntries(): Promise<void> {
    const entriesToRemove = this.cacheIndex.size - this.config.maxEntries;
    if (entriesToRemove <= 0) return;
    await this.evictOldestEntries(entriesToRemove);
  }

  async getContentCacheStats(): Promise<{
    totalEntries: number;
    totalSizeKB: number;
    oldestEntry: Date | null;
    newestEntry: Date | null;
  }> {
    await this.initialize();
    const keys = [...this.cacheIndex];
    let oldestTimestamp = Number.MAX_SAFE_INTEGER;
    let newestTimestamp = 0;
    let totalSize = 0;

    for (const cacheKey of keys) {
      try {
        const cached = await AsyncStorage.getItem(cacheKey);
        if (cached) {
          totalSize += cached.length * 2;
          const cachedContent: CachedContent = JSON.parse(cached);
          oldestTimestamp = Math.min(oldestTimestamp, cachedContent.timestamp);
          newestTimestamp = Math.max(newestTimestamp, cachedContent.timestamp);
        }
      } catch {
        // Skip invalid entries
      }
    }

    return {
      totalEntries: keys.length,
      totalSizeKB: Math.round(totalSize / 1024),
      oldestEntry: oldestTimestamp !== Number.MAX_SAFE_INTEGER ? new Date(oldestTimestamp) : null,
      newestEntry: newestTimestamp !== 0 ? new Date(newestTimestamp) : null,
    };
  }

  getTTL(contentType: keyof typeof CACHE_TTL_CONFIG): number {
    return CACHE_TTL_CONFIG[contentType] || this.config.defaultTTL;
  }

  async getSeriesCacheStats(campaignId: string): Promise<{
    totalSeriesCached: number;
    totalCacheEntries: number;
    seriesIds: string[];
  }> {
    await this.initialize();
    const keys = [...this.cacheIndex];
    const seriesIds: string[] = [];

    for (const cacheKey of keys) {
      if (cacheKey.includes(`${CACHE_PREFIX}${campaignId}_series_`)) {
        const seriesId = cacheKey.split('_series_')[1];
        if (seriesId) seriesIds.push(seriesId);
      }
    }

    return {
      totalSeriesCached: seriesIds.length,
      totalCacheEntries: keys.length,
      seriesIds,
    };
  }

  destroy(): void {
    if (this.cleanupTimerId) {
      clearInterval(this.cleanupTimerId);
      this.cleanupTimerId = null;
    }
    this.initialized = false;
  }
}

export const contentCacheService = new ContentCacheService();
